import axios from '../config/axiosConfig';

const API_URL = '/playbooks';

// Playbook categories
export const PLAYBOOK_CATEGORIES = [
  { value: 'reconnaissance', label: 'Reconnaissance' },
  { value: 'initial_access', label: 'Initial Access' },
  { value: 'execution', label: 'Execution' },
  { value: 'persistence', label: 'Persistence' },
  { value: 'privilege_escalation', label: 'Privilege Escalation' },
  { value: 'defense_evasion', label: 'Defense Evasion' },
  { value: 'credential_access', label: 'Credential Access' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'lateral_movement', label: 'Lateral Movement' },
  { value: 'collection', label: 'Collection' },
  { value: 'exfiltration', label: 'Exfiltration' },
  { value: 'impact', label: 'Impact' },
  { value: 'custom', label: 'Custom' }
];

// Executor types
export const EXECUTOR_TYPES = [
  { value: 'shell', label: 'Shell (Default)' },
  { value: 'powershell', label: 'PowerShell' },
  { value: 'cmd', label: 'Windows CMD' },
  { value: 'bash', label: 'Bash' },
  { value: 'sh', label: 'POSIX Shell' },
  { value: 'python', label: 'Python' },
  { value: 'manual', label: 'Manual (No execution)' }
];

// Platforms
export const PLATFORMS = [
  { value: 'all', label: 'All Platforms' },
  { value: 'windows', label: 'Windows' },
  { value: 'linux', label: 'Linux' },
  { value: 'darwin', label: 'macOS' }
];

// Execution status colors
export const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'text-green-500';
    case 'running':
      return 'text-blue-500';
    case 'pending':
      return 'text-yellow-500';
    case 'failed':
    case 'cancelled':
      return 'text-red-500';
    case 'partial':
      return 'text-orange-500';
    default:
      return 'text-gray-500';
  }
};

// Get all playbooks
const getPlaybooks = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.search) params.append('search', filters.search);
  if (filters.project) params.append('project', filters.project);
  if (filters.template) params.append('template', filters.template);

  const response = await axios.get(`${API_URL}?${params.toString()}`);
  return response.data;
};

// Get playbook categories with counts
const getCategories = async () => {
  const response = await axios.get(`${API_URL}/categories`);
  return response.data;
};

// Get templates
const getTemplates = async () => {
  const response = await axios.get(`${API_URL}/templates`);
  return response.data;
};

// Get single playbook
const getPlaybook = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Create playbook
const createPlaybook = async (data) => {
  const response = await axios.post(API_URL, data);
  return response.data;
};

// Update playbook
const updatePlaybook = async (id, data) => {
  const response = await axios.put(`${API_URL}/${id}`, data);
  return response.data;
};

// Delete playbook
const deletePlaybook = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Duplicate playbook
const duplicatePlaybook = async (id, project = null) => {
  const response = await axios.post(`${API_URL}/${id}/duplicate`, { project });
  return response.data;
};

// Execute playbook
const executePlaybook = async (id, data) => {
  const response = await axios.post(`${API_URL}/${id}/execute`, data);
  return response.data;
};

// Get executions
const getExecutions = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.playbook) params.append('playbook', filters.playbook);
  if (filters.project) params.append('project', filters.project);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await axios.get(`${API_URL}/executions/list?${params.toString()}`);
  return response.data;
};

// Get single execution
const getExecution = async (id) => {
  const response = await axios.get(`${API_URL}/executions/${id}`);
  return response.data;
};

// Cancel execution
const cancelExecution = async (id) => {
  const response = await axios.post(`${API_URL}/executions/${id}/cancel`);
  return response.data;
};

// Create playbook from atomic tests
const createFromAtomic = async (data) => {
  const response = await axios.post(`${API_URL}/from-atomic`, data);
  return response.data;
};

// Helper: Create empty step
const createEmptyStep = (order = 1) => ({
  order,
  name: '',
  description: '',
  executorType: 'shell',
  command: '',
  arguments: [],
  timeout: 300,
  cleanupCommand: '',
  platforms: ['all'],
  requiresAdmin: false,
  variables: [],
  techniqueId: '',
  techniqueName: '',
  tactic: '',
  atomicTestId: null,
  atomicTestName: '',
  atomicTestGuid: '',
  continueOnError: false,
  delayAfter: 0,
  condition: { type: 'always' }
});

// Helper: Create empty playbook
const createEmptyPlaybook = () => ({
  name: '',
  description: '',
  category: 'custom',
  tags: [],
  version: '1.0.0',
  targetPlatforms: ['all'],
  steps: [createEmptyStep(1)],
  variables: [],
  isPublic: false,
  isTemplate: false
});

const playbookService = {
  getPlaybooks,
  getCategories,
  getTemplates,
  getPlaybook,
  createPlaybook,
  updatePlaybook,
  deletePlaybook,
  duplicatePlaybook,
  executePlaybook,
  getExecutions,
  getExecution,
  cancelExecution,
  createFromAtomic,
  createEmptyStep,
  createEmptyPlaybook,
  getStatusColor,
  PLAYBOOK_CATEGORIES,
  EXECUTOR_TYPES,
  PLATFORMS
};

export default playbookService;
